<template>
    <div class="block w-full">
        <div v-if="mapsAreLoading" class="flex flex-col items-center justify-center py-6">
            <img src="/assets/img/map-list-loading.gif" class="block mb-4">
            <p class="text-cool-gray-500">Loading, please wait....</p>
        </div>
        <div v-else-if="maps.length" class="map-list" :class="{ 'map-list--imgless': !imagesVisible }">

            <header class="bg-white rounded shadow p-4 mb-6">

                <div class="w-full flex flex-col md:flex-row md:items-center">
                    <div class="w-full flex flex-col sm:flex-row md:w-2/3">

                        <div class="w-full mb-2 sm:w-1/2 sm:mb-3 sm:pr-1 md:mb-0 md:pr-4">
                            <div class="select-wrap">
                                <select name="filter_series" id="filter_series" class="select leading-tight" v-model="filterSeries">
                                    <option value="">All series</option>
                                    <option v-for="seriesOpt in availableSeries" :value="seriesOpt.id">{{ seriesOpt.name }}</option>
                                </select>
                                <div class="select-chevron">
                                    <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <div class="w-full mb-2 sm:w-1/2 sm:mb-3 sm:pl-1 pl:pl-0 md:mb-0 md:pr-4">
                            <div class="select-wrap">
                                <select name="filter_misprints" id="filter_misprints" class="select leading-tight" v-model="filterMisprints">
                                    <option value="include">Include misprints</option>
                                    <option value="exclude">Exclude misprints</option>
                                    <option value="only">Only show misprints</option>
                                </select>
                                <div class="select-chevron">
                                    <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                                    </svg>
                                </div>
                            </div>
                        </div>

                    </div>
                    <div class="w-full md:w-1/3">
                        <input autofocus v-model="filterSearch" name="q" id="maps_q" placeholder="Search..." type="search" class="block w-full outline-none appearance-none rounded py-2 px-3 bg-cool-gray-50 text-cool-gray-700 leading-tight border hover:border-cool-gray-400 focus:outline-none focus:border-indigo-400" autocomplete="off">
                    </div>
                </div>

                <div class="w-full flex flex-col -mb-1 md:flex-row md:justify-between md:items-center md:pt-4 lg:pt-5">

                    <div v-if="authenticated" class="flex items-center justify-center flex-wrap text-cool-gray-600 -mx-2 border-b border-cool-gray-200 py-3 md:border-b-0 md:py-0">
                        <label class="flex items-center px-2 cursor-pointer hover:text-cool-gray-800" for="filter_all">
                            <input v-model="filterOwnership" type="radio" name="filter" id="filter_all" value="all">
                            <span class="block pl-2">All</span>
                        </label>
                        <label class="flex items-center px-2 cursor-pointer hover:text-cool-gray-800" for="filter_mine">
                            <input v-model="filterOwnership" type="radio" name="filter" id="filter_mine" value="mine">
                            <span class="block pl-2">Mine</span>
                        </label>
                        <label class="flex items-center px-2 cursor-pointer hover:text-cool-gray-800" for="filter_duplicates">
                            <input v-model="filterOwnership" type="radio" name="filter" id="filter_duplicates" value="duplicates">
                            <span class="block pl-2">Duplicates</span>
                        </label>
                        <label class="flex items-center px-2 cursor-pointer hover:text-cool-gray-800" for="filter_needed">
                            <input v-model="filterOwnership" type="radio" name="filter" id="filter_needed" value="needed">
                            <span class="block pl-2">Needed</span>
                        </label>
                    </div>

                    <div class="flex items-center justify-center text-cool-gray-600 pt-3 md:pt-0">
                        <label for="sort_asc" class="flex items-center cursor-pointer hover:text-cool-gray-800 mr-5">
                            <input type="radio" id="sort_asc" name="sort" value="asc" v-model="optOrder" v-on:change="reorderMaps">
                            <span class="block ml-2">Oldest first</span>
                        </label>
                        <label for="sort_desc" class="flex items-center cursor-pointer hover:text-cool-gray-800">
                            <input type="radio" id="sort_desc" name="sort" value="desc" v-model="optOrder" v-on:change="reorderMaps">
                            <span class="block ml-2">Newest first</span>
                        </label>
                    </div>

                </div>

            </header>

            <main class="map-list-items block" v-if="!switchingOrder && visibleMapIds.length">
                <a v-for="map in maps" v-show="visibleMapIds.includes(map.id)" :href="map.url" :title="map.label" class="flex relative justify-start lg:justify-between w-full p-2 mb-2 bg-white shadow hover:shadow-md rounded border border-white" :class="{ 'hover:border-indigo-300': !authenticated, 'bg-green-50 border-green-300 hover:border-green-400': (authenticated && map.user_pieces_count>0), 'bg-red-50 border-red-300 hover:border-red-400': (authenticated && map.user_pieces_count<=0) }">
                    <span v-if="authenticated" class="absolute right-0 top-0 text-xs leading-none py-1 w-6 text-center rounded-bl text-white font-medium" :class="{ 'bg-red-400': !map.user_pieces_count, 'bg-green-400': map.user_pieces_count>0}">{{ (map.user_pieces_count>0 ? map.user_pieces_count : 0) }}</span>
                    <div v-if="imagesVisible" class="map-list-w-thumb flex items-center justify-center flex-shrink-0 rounded-sm mr-2 text-white" :class="{ 'bg-gray-200': !map.cover_img_urls }">
                        <img v-if="map.cover_img_urls" :data-src="map.cover_img_urls['thumb-1x']" :data-srcset="map.cover_img_urls['thumb-1x'] + ' 1x, ' + map.cover_img_urls['thumb-2x'] + ' 2x'" :alt="map.label" :title="map.label" class="rounded-sm object-fill block max-w-full max-h-full um-lazyload">
                    </div>
                    <div class="flex flex-grow flex-column lg:flex-row flex-wrap items-start justify-start">
                        <div class="map-list-w-version w-full lg:h-full lg:pr-2 lg:border-r">
                            <p class="hidden lg:block text-cool-gray-400 text-xs mb-1">Date &amp; Edition</p>
                            <div class="w-full flex pb-2 border-b lg:block lg:p-0 lg:border-none">
                                <p class="text-cool-gray-400 w-30 flex-shrink-0 lg:hidden">Date</p>
                                <p class="sm:pl-2 md:pl-4 lg:pl-0">{{ map.display_at_short_label }}</p>
                            </div>
                            <div class="w-full flex py-2 border-b lg:block lg:p-0 lg:border-none">
                                <p class="text-cool-gray-400 w-30 flex-shrink-0 lg:hidden">Edition</p>
                                <p class="sm:pl-2 md:pl-4 lg:pl-0">{{ map.edition }}</p>
                            </div>
                        </div>
                        <div class="map-list-w-cover-title w-full flex lg:block py-2 lg:py-0 lg:px-2 border-b lg:border-b-0 lg:border-r lg:h-full">
                            <p class="text-cool-gray-400 w-30 lg:w-full flex-shrink-0 lg:text-xs lg:mb-1">Cover Title</p>
                            <p class="sm:pl-2 md:pl-4 lg:pl-0">{{ map.cover_title }}</p>
                        </div>
                        <div class="map-list-w-cover-design w-full flex lg:block py-2 lg:py-0 lg:px-2 border-b lg:border-b-0 lg:border-r lg:h-full">
                            <p class="text-cool-gray-400 w-30 lg:w-full flex-shrink-0 lg:text-xs lg:mb-1">Cover Design</p>
                            <p class="sm:pl-2 md:pl-4 lg:pl-0">{{ map.cover_design }}</p>
                        </div>
                        <div class="map-list-w-variant w-full flex lg:block py-2 lg:py-0 lg:px-2 border-b lg:border-b-0 lg:border-r lg:h-full">
                            <p class="text-cool-gray-400 w-30 lg:w-full flex-shrink-0 lg:text-xs lg:mb-1">Variant</p>
                            <p class="sm:pl-2 md:pl-4 lg:pl-0">{{ map.variant }}</p>
                        </div>
                        <div class="map-list-w-designer w-full flex lg:block py-2 lg:py-0 lg:px-2 border-b lg:border-b-0 lg:border-r lg:h-full">
                            <p class="text-cool-gray-400 w-30 lg:w-full flex-shrink-0 lg:text-xs lg:mb-1">Designer</p>
                            <p class="sm:pl-2 md:pl-4 lg:pl-0">{{ map.designer_name }}</p>
                        </div>
                        <div class="map-list-w-reference w-full flex lg:block pt-2 lg:py-0 lg:pl-2 lg:h-full">
                            <p class="text-cool-gray-400 w-30 lg:w-full flex-shrink-0 lg:text-xs lg:mb-1">Reference</p>
                            <p class="sm:pl-2 md:pl-4 lg:pl-0 break-all">{{ map.reference }}</p>
                        </div>
                    </div>
                </a>
            </main>

            <aside v-else class="text-center py-10">
                <p class="text-cool-gray-600 mb-2">There are no maps that match your filters.</p>
                <p><a v-on:click.prevent="resetFilters" href="#" class="link link--primary underline">Reset filters</a></p>
            </aside>

        </div>
        <div v-else>
            <p class="text-center text-red-700">There has been an error.</p>
        </div>
    </div>
</template>

<script>
    export default {
        props: {
            authenticated: {
                type: Boolean,
                default: false,
            },
            imagesVisible: {
                type: Boolean,
                default: false,
            },
            initialSeries: {
                type: String,
                required: false,
            },
            initialMisprints: {
                type: String,
                required: false,
            },
            initialSearch: {
                type: String,
                required: false,
            },
            initialOwnership: {
                type: String,
                required: false,
            },
            initialOrder: {
                type: String,
                required: false,
            },
            availableSeries: {
                type: Array,
                require: false,
                default: [],
            }
        },
        data: function () {
            return {
                maps:               [],
                mapsAreLoading:     false,
                mapsOrder:          'desc',
                optOrder:           'desc',
                filterSeries:       "",
                filterMisprints:    'include',
                filterSearch:       null,
                filterOwnership:    'all',
                switchingOrder:     false,
            }
        },
        computed: {
            visibleMapIds: function()
            {
                let maps = this.maps;

                if(this.filterMisprints && this.filterMisprints!=='include')
                {
                    if(this.filterMisprints==='exclude')
                    {
                        maps = maps.filter((map) => {
                            return map.is_misprint === false;
                        });
                    }
                    else if(this.filterMisprints==='only')
                    {
                        maps = maps.filter((map) => {
                            return map.is_misprint === true;
                        });
                    }
                }

                if(this.filterSeries.length)
                {
                    maps = maps.filter((map) => {
                        return map.series_id === this.filterSeries;
                    });
                }

                if(this.authenticated && this.filterOwnership!=='all')
                {
                    if(this.filterOwnership==='mine')
                    {
                        maps = maps.filter((map) => {
                            return map.user_pieces_count > 0;
                        });
                    }
                    else if(this.filterOwnership==='duplicates')
                    {
                        maps = maps.filter((map) => {
                            return map.user_pieces_count > 1;
                        });
                    }
                    else if(this.filterOwnership==='needed')
                    {
                        maps = maps.filter((map) => {
                            return !map.user_pieces_count;
                        });
                    }
                }

                if(!this.filterSearch || !this.filterSearch.trim().length)
                {
                    return maps.map(map => map.id);
                }

                let filterSearch = this.filterSearch.toLowerCase();

                const uuidV4Regex = /^[A-F\d]{8}-[A-F\d]{4}-4[A-F\d]{3}-[89AB][A-F\d]{3}-[A-F\d]{12}$/i;
                let filterSearchIsUuid = uuidV4Regex.test(filterSearch);

                return maps.filter((map) => {
                    return map._searchIndex.includes(filterSearch) || (filterSearchIsUuid && map.id===filterSearch);
                }).map(map => map.id);
            }
        },
        watch: {
            filterSeries() {
                this.setHistory();
            },
            filterMisprints() {
                this.setHistory();
            },
            filterSearch() {
                this.setHistory();
            },
            filterOwnership() {
                this.setHistory();
            },
            mapsOrder() {
                this.setHistory();
                this.saveOrderPreference();
            },
            visibleMapIds() {
                this.updateImages();
            },
        },
        created()
        {
            if(this.initialSeries && this.initialSeries.trim().length)
            {
                this.filterSeries = this.initialSeries.trim();
            }

            if(this.initialMisprints && ['include', 'exclude', 'only'].includes(this.initialMisprints.trim()))
            {
                this.filterMisprints = this.initialMisprints.trim();
            }

            if(this.initialSearch && this.initialSearch.trim().length)
            {
                this.filterSearch = this.initialSearch.trim();
            }

            if(this.initialOwnership && ['all', 'mine', 'duplicates', 'needed'].includes(this.initialOwnership.trim()))
            {
                this.filterOwnership = this.initialOwnership.trim();
            }

            if(this.initialOrder && ['asc', 'desc'].includes(this.initialOrder.trim()))
            {
                this.optOrder = this.initialOrder.trim();
                this.mapsOrder = this.initialOrder.trim();
            }

            this.loadMaps();
        },
        methods: {
            loadMaps()
            {
                if(this.mapsAreLoading===true)
                {
                    return;
                }

                this.mapsAreLoading = true;

                axios.post("/xhr/maps", {}).then(response => {
                    this.maps = response.data.map(function(map) {
                        let { id, cover_img_urls, url, ...searchIndex } = map;
                        map._searchIndex = Object.values(searchIndex).join(" ").toLowerCase();
                        return map;
                    });
                    this.mapsVisible = this.maps.map(map => map.id);
                    this.reorderMaps();
                    this.mapsAreLoading = false;
                    this.updateImages();
                }).catch((error) => {
                    this.maps = [];
                    this.mapsAreLoading = false;
                });
            },

            resetFilters()
            {
                this.filterSeries = '';
                this.filterMisprints = 'include';
                this.filterSearch = null;
                this.filterOwnership = 'all';
                this.updateImages();
            },

            reorderMaps()
            {
                if(this.mapsOrder!==this.optOrder || (this.mapsAreLoading && this.mapsOrder==='desc'))
                {
                    this.switchingOrder = true;
                    this.mapsOrder = this.optOrder;
                    this.$nextTick(() => {
                        this.maps.reverse();
                        this.switchingOrder = false;
                    });
                }
            },

            setHistory()
            {
                let url = "/maps?q=" +
                    (this.filterSearch!==null && this.filterSearch.trim().length ? encodeURIComponent(this.filterSearch) : '') +
                    '&series=' + this.filterSeries +
                    '&misprints=' + this.filterMisprints +
                    '&ownership=' + this.filterOwnership +
                    '&order=' + this.mapsOrder;

                history.replaceState({}, "List of Underground Tube Maps", url);
            },

            updateImages()
            {
                this.$nextTick(() => {
                    window.umLazyload.update();
                });
            },

            saveOrderPreference()
            {
                if(this.authenticated)
                {
                    axios.post("/xhr/account/order-preference", {
                        order: this.optOrder,
                    }).then(response => {

                    }).catch((error) => {

                    });
                }
            }
        },
    };
</script>
